import { useMutation } from "@apollo/client";
import {
  Box,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Button,
} from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { currentTierIIReportingYear } from "encamp-shared/src/constants/tierii";
import { gql } from "generated-graphql";
import { TierIiReportKind } from "generated-graphql/graphql";
import { useCreateAnnual } from "hooks/useCreateReport";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { REPORTS_QUERY, ReportRow } from ".";
import pluralize from "pluralize";

const BULK_OPEN_ANNUAL_REPORT = gql(`
  mutation OpenAnnualReports($reportingYear: Int!, $reportKind: TierIIReportKind!, $facilityIds: [ID!]!) {
    createTierIIReports(reportingYear: $reportingYear, reportKind: $reportKind, facilityIds: $facilityIds) {
      id
    }
  }
`);

type CreateAnnualReportDialogProps = {
  onClose: () => void;
  reports: ReportRow[];
};

export const CreateAnnualReportDialog = ({
  onClose,
  reports,
}: CreateAnnualReportDialogProps) => {
  const [reportingYear, setReportingYear] = useState(
    reports[0].reportingYear ?? currentTierIIReportingYear
  );
  const [showConfirmation, setShowConfirmation] = useState(false);
  const alerts = useAlerts();
  const navigate = useNavigate();
  const [createSingleReport] = useCreateAnnual();
  const [bulkCreateReports] = useMutation(BULK_OPEN_ANNUAL_REPORT, {
    refetchQueries: [REPORTS_QUERY],
  });

  // Filter out reports that already have a tierIIReportId
  const reportsToCreate = reports.filter((report) => !report.tierIIReportId);

  const handleYearSelection = () => {
    if (reportsToCreate.length === 0) {
      alerts.error("All selected facilities already have annual reports.");
      onClose();
      return;
    }
    setShowConfirmation(true);
  };

  const handleCreateReports = async () => {
    try {
      if (reportsToCreate.length === 1) {
        // Single report creation
        alerts.info("Creating Annual Report");
        await createSingleReport({
          variables: {
            facilityId: reportsToCreate[0].facilityId,
            reportingYear,
          },
          onCompleted: (data) => {
            navigate(`${data.createTierIIReport.id}`);
            onClose();
          },
          onError: (err) => {
            alerts.error("Error creating Annual Report", err);
            onClose();
          },
        });
      } else {
        // Bulk report creation
        alerts.info("Starting bulk report initialization");
        await bulkCreateReports({
          variables: {
            reportingYear,
            reportKind: TierIiReportKind.Annual,
            facilityIds: reportsToCreate.map((report) => report.facilityId),
          },
          onCompleted: () => {
            alerts.success(
              `Successfully created ${reportsToCreate.length} annual reports for RY${reportingYear}.`
            );
            onClose();
          },
          onError: (err) => {
            alerts.error(
              "There was an error during bulk report initialization"
            );
            console.error(err);
            onClose();
          },
        });
      }
    } catch (err) {
      alerts.error("An error occurred while creating reports");
      console.error(err);
      onClose();
    }
  };

  // Generate year options - current year plus next 2 years
  const yearOptions = [
    currentTierIIReportingYear - 1,
    currentTierIIReportingYear,
    currentTierIIReportingYear + 1,
  ];

  if (!showConfirmation) {
    return (
      <Dialog
        open
        onClose={onClose}
        title="Select Reporting Year"
        primaryActionButtonProps={{
          onClick: handleYearSelection,
          saveText: "Next",
          startIcon: null,
        }}
      >
        <Typography gutterBottom>
          Select the reporting year for the annual report
          {reportsToCreate.length > 1 ? "s" : ""}.
        </Typography>
        <Select
          fullWidth
          value={reportingYear}
          onChange={(e) => {
            // ast-grep-ignore: no-unsafe-number-parsing
            setReportingYear(Number(e.target.value));
          }}
        >
          {yearOptions.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Confirm Annual Report Creation"
      primaryActionButtonProps={{
        onClick: handleCreateReports,
        saveText: pluralize("Create Report", reportsToCreate.length),
        startIcon: null,
      }}
      secondaryActionElements={[
        <Button key="back" onClick={() => setShowConfirmation(false)}>
          Back
        </Button>,
      ]}
      maxWidth="md"
      fullWidth
    >
      <Box sx={{ mb: 2 }}>
        <Typography gutterBottom>
          {reportsToCreate.length === 1
            ? "Are you sure you want to create a RY" +
              reportingYear +
              " annual report for this facility?"
            : "Are you sure you want to create RY" +
              reportingYear +
              " annual reports for these facilities?"}
        </Typography>
      </Box>
      <TableContainer sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Facility Name</TableCell>
              <TableCell>Facility ID</TableCell>
              <TableCell>Address</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportsToCreate.map((report) => (
              <TableRow key={report.facilityId}>
                <TableCell>{report.facilityName}</TableCell>
                <TableCell>{report.facilityId || "-"}</TableCell>
                <TableCell>
                  {report.streetAddress1 && `${report.streetAddress1}, `}
                  {report.city && `${report.city}, `}
                  {report.state} {report.zip}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Dialog>
  );
};
